import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MarketingConsentStatus, PERMISSIONS } from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader } from '@/components/ui/Card';
import { LoadingState } from '@/components/ui/LoadingState';
import { usePermissions } from '@/hooks/usePermissions';
import { apiErrorMessage } from '@/services/apiClient';
import {
  useContactPreferencesQuery,
  usePreferenceHistoryQuery,
  useSavePreferencesMutation,
} from '../api/crm.api';

const CONSENT_TONE: Record<string, 'neutral' | 'success' | 'danger'> = {
  NOT_GRANTED: 'neutral',
  GRANTED: 'success',
  REVOKED: 'danger',
};

/**
 * Whether this customer may be messaged, and about what.
 *
 * It lives on the customer rather than in a CRM settings screen because that is what it is:
 * something this person said about themselves, not a property of the messaging system.
 *
 * The wording of the marketing states matters more than it looks. The default is `NOT_GRANTED`, and
 * calling that "No" would read as a decision the customer made — they did not; nobody has asked
 * them. `REVOKED` is the one that means they said no, and keeping the two visibly distinct is what
 * stops somebody later treating silence as refusal, or worse, refusal as silence.
 */
export function ContactPreferencesCard({ customerId }: { customerId: string }) {
  const { t, i18n } = useTranslation();
  const { can } = usePermissions();
  const canEdit = can(PERMISSIONS.CUSTOMERS_EDIT);

  const [failure, setFailure] = useState<string | null>(null);
  const preferences = useContactPreferencesQuery(customerId);
  const history = usePreferenceHistoryQuery(customerId);
  const save = useSavePreferencesMutation(customerId);

  const current = preferences.data;

  const apply = (input: {
    operationalMessagingSuppressed?: boolean;
    marketingConsentStatus?: string;
  }) => {
    if (!current) return;
    setFailure(null);
    save.mutate(
      { ...input, version: current.version },
      { onError: (error) => setFailure(apiErrorMessage(error) ?? t('crm.preferences.saveFailed')) },
    );
  };

  return (
    <Card>
      <CardHeader title={t('crm.preferences.title')} />
      <div className="space-y-4 p-4 pt-0">
        {failure ? <Alert tone="error">{failure}</Alert> : null}

        {preferences.isPending || !current ? (
          <LoadingState />
        ) : (
          <>
            <div className="border-line flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3">
              <div className="min-w-0">
                <p className="text-sm font-medium">{t('crm.preferences.operational')}</p>
                <p className="text-muted text-xs">{t('crm.preferences.operationalHint')}</p>
              </div>
              <div className="flex flex-wrap items-center justify-end gap-2">
                <Badge tone={current.operationalMessagingSuppressed ? 'warning' : 'success'}>
                  {t(
                    current.operationalMessagingSuppressed
                      ? 'crm.preferences.operationalSuppressed'
                      : 'crm.preferences.operationalAllowed',
                  )}
                </Badge>
                {canEdit ? (
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={save.isPending}
                    onClick={() =>
                      apply({
                        operationalMessagingSuppressed: !current.operationalMessagingSuppressed,
                      })
                    }
                  >
                    {t(
                      current.operationalMessagingSuppressed
                        ? 'crm.preferences.allow'
                        : 'crm.preferences.suppress',
                    )}
                  </Button>
                ) : null}
              </div>
            </div>

            <div className="border-line flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3">
              <div className="min-w-0">
                <p className="text-sm font-medium">{t('crm.preferences.marketing')}</p>
                <p className="text-muted text-xs">{t('crm.preferences.marketingHint')}</p>
              </div>
              <div className="flex flex-wrap items-center justify-end gap-2">
                <Badge tone={CONSENT_TONE[current.marketingConsentStatus] ?? 'neutral'}>
                  {t(`crm.consent.${current.marketingConsentStatus}`)}
                </Badge>
                {canEdit ? (
                  <>
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={save.isPending || current.marketingConsentStatus === 'GRANTED'}
                      onClick={() =>
                        apply({ marketingConsentStatus: MarketingConsentStatus.GRANTED })
                      }
                    >
                      {t('crm.preferences.grant')}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={save.isPending || current.marketingConsentStatus === 'REVOKED'}
                      onClick={() =>
                        apply({ marketingConsentStatus: MarketingConsentStatus.REVOKED })
                      }
                    >
                      {t('crm.preferences.revoke')}
                    </Button>
                  </>
                ) : null}
              </div>
            </div>

            {!canEdit ? (
              <p className="text-muted text-sm">{t('crm.preferences.readOnly')}</p>
            ) : null}

            <section>
              <h3 className="mb-2 text-sm font-semibold">{t('crm.preferences.history')}</h3>
              {(history.data?.length ?? 0) === 0 ? (
                <p className="text-muted text-sm">{t('crm.preferences.noHistory')}</p>
              ) : (
                <ul className="text-muted space-y-1 text-xs">
                  {history.data?.map((change) => (
                    <li key={change.id} className="flex justify-between gap-2">
                      <span>
                        {t(`crm.consent.${change.newValue}`, { defaultValue: change.newValue })}
                      </span>
                      <span dir="ltr">
                        {new Date(change.changedAt).toLocaleString(i18n.language)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </>
        )}
      </div>
    </Card>
  );
}
